// components/features/support/tasks/AddNoteModal.tsx
import React, { useEffect, useState } from "react";
import { Loader2, MessageSquarePlus } from "lucide-react";

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialNote?: string | null;
  taskTitle?: string;
  onSave: (note: string) => Promise<void>;
}

export const AddNoteModal: React.FC<AddNoteModalProps> = ({ isOpen, onClose, initialNote, taskTitle, onSave }) => {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || "");
      setError(null);
    }
  }, [isOpen, initialNote]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSave(note.trim());
      onClose();
    } catch (err) {
      console.error(err);
      setError("Impossible d'enregistrer la note");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <MessageSquarePlus className="w-5 h-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-900">
              {initialNote ? "Modifier la note" : "Ajouter une note"}
            </h2>
          </div>

          <div className="px-5 py-4 space-y-3">
            {taskTitle && (
              <p className="text-xs text-gray-500">
                Tâche : <span className="font-medium text-gray-700">{taskTitle}</span>
              </p>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg border border-red-200">{error}</div>
            )}

            <textarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              placeholder="Avancement, blocage, résolution, remarque..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <div className="flex justify-end">
              <span className="text-xs text-gray-400">{note.length} caractères</span>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm bg-primary text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
